// Namen, Standardwerte, Kurztexte und die Vollständigkeitsprüfung der Workflows (Agent 4C). Die Prüfung spiegelt
// `api/crm_api/workflows/definition.py`: was hier fehlt, lehnt der Server beim Aktivieren ab.
import type { Ausloeser, AusloeserArt, Bedingung, LaufStatus, Schritt, SchrittArt, Vergleich, WorkflowStatus } from "../api/workflows";
import type { IconName } from "../components/Icon";

export const AUSLOESER: { art: AusloeserArt; label: string; text: string }[] = [
  { art: "manuell", label: "Manuell", text: "Von Hand über Blitz ▾ an einer Smart View oder am Lead" },
  { art: "statuswechsel", label: "Statuswechsel", text: "Sobald ein Lead in einen bestimmten Status wechselt" },
  { art: "lead_angelegt", label: "Lead angelegt", text: "Sobald ein neuer Lead entsteht" },
  { art: "custom_activity", label: "Custom Activity", text: "Sobald eine Custom Activity gespeichert wird" },
];

export const SCHRITTE: { art: SchrittArt; label: string; icon: IconName; text: string }[] = [
  { art: "aufgabe", label: "Aufgabe", icon: "aufgabe", text: "Legt ein Follow-up am Lead an" },
  { art: "anrufaufgabe", label: "Anrufaufgabe", icon: "telefon", text: "Legt eine Aufgabe mit Anruf-Symbol an" },
  { art: "email_entwurf", label: "E-Mail-Entwurf", icon: "mail", text: "Bereitet eine Mail zur Prüfung vor, sendet nie" },
  { art: "sms", label: "SMS-Entwurf", icon: "sms", text: "Bereitet eine SMS zur Prüfung vor, sendet nie" },
  { art: "zuweisen", label: "Zuweisen", icon: "person", text: "Setzt den Bearbeiter des Leads" },
  { art: "lead_aendern", label: "Lead ändern", icon: "stift", text: "Setzt Status oder Felder des Leads" },
  { art: "filter", label: "Filter", icon: "filter", text: "Beendet den Lauf, wenn der Lead nicht passt" },
  { art: "verzoegerung", label: "Verzögerung", icon: "uhr", text: "Wartet, bevor der nächste Schritt läuft" },
];

export const schrittInfo = (art: SchrittArt) => SCHRITTE.find((s) => s.art === art) ?? SCHRITTE[0];
export const ausloeserInfo = (art: AusloeserArt) => AUSLOESER.find((a) => a.art === art) ?? AUSLOESER[0];

export const WORKFLOW_STATUS: Record<WorkflowStatus, string> = { entwurf: "Entwurf", aktiv: "Aktiv", pausiert: "Pausiert", archiviert: "Archiviert" };
export const LAUF_STATUS: Record<LaufStatus, string> = { wartet: "Wartet", laeuft: "Läuft", pausiert: "Pausiert", fertig: "Fertig", fehler: "Fehler", abgebrochen: "Beendet" };

export const VERGLEICHE: Record<Vergleich, string> = {
  ist: "ist",
  ist_nicht: "ist nicht",
  in: "ist eines von",
  nicht_in: "ist keines von",
  enthaelt: "enthält",
  enthaelt_nicht: "enthält nicht",
  gefuellt: "ist ausgefüllt",
  leer: "ist leer",
  groesser: "ist größer als",
  kleiner: "ist kleiner als",
};

export function neueSchrittId(schritte: Schritt[]): string {
  const belegt = new Set(schritte.map((s) => s.id));
  let n = schritte.length + 1;
  while (belegt.has(`s${n}`)) n += 1;
  return `s${n}`;
}

export function neuerSchritt(art: SchrittArt, schritte: Schritt[]): Schritt {
  const config: Record<string, unknown> = (() => {
    switch (art) {
      case "aufgabe":
      case "anrufaufgabe":
        return { text: art === "anrufaufgabe" ? "Anrufen" : "", tage: 0, uhrzeit: null, zustaendig: "ausfuehrender", nutzer_id: null, prioritaet: null, pflicht: false };
      case "email_entwurf":
        return { vorlage_id: null, betreff: "", text: "", absender_user_id: null, empfaenger: "erster", aufgabe: true };
      case "sms":
        return { vorlage_id: null, text: "", nutzer_id: null, telefon: "mobil" };
      case "zuweisen":
        return { modus: "nutzer", nutzer_id: null, nutzer_ids: [], ueberschreiben: true };
      case "lead_aendern":
        return { status_id: null, felder: {}, ueberschreiben: true };
      case "filter":
        return { bedingungen: [] };
      case "verzoegerung":
        return { minuten: 0, stunden: 0, tage: 1, uhrzeit: null, nur_werktage: false };
    }
  })();
  return { id: neueSchrittId(schritte), art, name: null, config };
}

export const leererAusloeser = (art: AusloeserArt): Ausloeser => ({
  art,
  config: art === "statuswechsel" ? { von: [], nach: [] } : art === "custom_activity" ? { type_id: null, bedingungen: [] } : {},
});

export function leereBedingung(): Bedingung {
  return { feld: "status_id", op: "ist", wert: "" };
}

const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const zahl = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);

/** Warum der Schritt noch nichts tun kann (spiegelt `definition.unvollstaendig_schritt`), sonst null. */
export function schrittProblem(s: Schritt): string | null {
  const c = s.config;
  switch (s.art) {
    case "email_entwurf":
      return !text(c.vorlage_id) && !(text(c.betreff) && text(c.text)) ? "Vorlage oder Betreff und Text fehlen." : null;
    case "sms":
      return !text(c.vorlage_id) && !text(c.text) ? "Vorlage oder Text fehlen." : null;
    case "aufgabe":
    case "anrufaufgabe":
      if (!text(c.text)) return "Der Aufgabentext fehlt.";
      return c.zustaendig === "nutzer" && !text(c.nutzer_id) ? "Es ist kein Nutzer gewählt." : null;
    case "zuweisen":
      if (c.modus === "nutzer" && !text(c.nutzer_id)) return "Es ist kein Nutzer gewählt.";
      if (c.modus === "zufall" && !((c.nutzer_ids as string[] | undefined)?.length ?? 0)) return "Es sind keine Nutzer für die Zufallswahl gewählt.";
      return null;
    case "lead_aendern":
      return !text(c.status_id) && !Object.keys((c.felder as object | undefined) ?? {}).length ? "Weder Status noch Feld ist gewählt." : null;
    case "filter":
      return !((c.bedingungen as unknown[] | undefined)?.length ?? 0) ? "Es ist keine Bedingung eingetragen." : null;
    case "verzoegerung":
      return zahl(c.minuten) + zahl(c.stunden) * 60 + zahl(c.tage) * 1440 < 1 ? "Die Wartezeit ist 0." : null;
  }
}

export function ausloeserProblem(a: Ausloeser): string | null {
  if (a.art === "statuswechsel") {
    const c = a.config as { von?: string[]; nach?: string[] };
    return !(c.von?.length || c.nach?.length) ? "Es ist kein Status gewählt." : null;
  }
  if (a.art === "custom_activity") return !text((a.config as { type_id?: string }).type_id) ? "Es ist kein Typ gewählt." : null;
  return null;
}

export function dauerKurz(c: Record<string, unknown>): string {
  const teile: string[] = [];
  const t = zahl(c.tage);
  const h = zahl(c.stunden);
  const m = zahl(c.minuten);
  if (t) teile.push(t === 1 ? "1 Tag" : `${t} Tage`);
  if (h) teile.push(h === 1 ? "1 Stunde" : `${h} Stunden`);
  if (m) teile.push(m === 1 ? "1 Minute" : `${m} Minuten`);
  let r = teile.join(" ") || "0 Minuten";
  if (text(c.uhrzeit)) r += ` bis ${text(c.uhrzeit)} Uhr`;
  if (c.nur_werktage) r += ", nur Werktage";
  return r;
}

export function faelligKurz(c: Record<string, unknown>): string {
  const t = zahl(c.tage);
  const basis = t === 0 ? "heute" : t === 1 ? "morgen" : `in ${t} Tagen`;
  return text(c.uhrzeit) ? `${basis}, ${text(c.uhrzeit)} Uhr` : basis;
}
